import { useCallback } from "react";
import { useReactiveVar } from "@apollo/client";
import {
    CompanyAccess,
    PermissionScope,
    StorageProductRelationHistoryAction,
    StorageProductRelationHistoryProxy,
} from "@me-team/host/main/graphql/types";
import { currentBranchEmployeeIdVar } from "@me-team/host/main/globalVar/state";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

type ScopedPermissionAccess = {
    all: boolean;
    self: boolean;
};

const resolveScopedAccess = (
    currentBranchEmployeeId: number | string | null | undefined,
    targetEmployeeId: number | string | null | undefined,
    access: ScopedPermissionAccess
): boolean => {
    if (access.all) {
        return true;
    }

    if (!access.self || !currentBranchEmployeeId || !targetEmployeeId) {
        return false;
    }

    if (Number(currentBranchEmployeeId) === Number(targetEmployeeId)) {
        return true;
    }

    return false;
};

export const useWarehouseOperationsAccess = () => {
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);

    const canEditArrivalAll = useRoleAccess(CompanyAccess.WarehouseOperationArrivalEdit, {
        scope: PermissionScope.All,
    });
    const canEditArrivalSelf = useRoleAccess(CompanyAccess.WarehouseOperationArrivalEdit, {
        scope: PermissionScope.Self,
    });
    const canEditConsumablesWriteOffAll = useRoleAccess(CompanyAccess.WarehouseOperationConsumablesWriteOffEdit, {
        scope: PermissionScope.All,
    });
    const canEditConsumablesWriteOffSelf = useRoleAccess(CompanyAccess.WarehouseOperationConsumablesWriteOffEdit, {
        scope: PermissionScope.Self,
    });
    const canEditMoveAll = useRoleAccess(CompanyAccess.WarehouseOperationMoveEdit, {
        scope: PermissionScope.All,
    });
    const canEditMoveSelf = useRoleAccess(CompanyAccess.WarehouseOperationMoveEdit, {
        scope: PermissionScope.Self,
    });
    const canEditProductWriteOffAll = useRoleAccess(CompanyAccess.WarehouseOperationProductWriteOffEdit, {
        scope: PermissionScope.All,
    });
    const canEditProductWriteOffSelf = useRoleAccess(CompanyAccess.WarehouseOperationProductWriteOffEdit, {
        scope: PermissionScope.Self,
    });
    const canEditSaleAll = useRoleAccess(CompanyAccess.WarehouseOperationSaleEdit, {
        scope: PermissionScope.All,
    });
    const canEditSaleSelf = useRoleAccess(CompanyAccess.WarehouseOperationSaleEdit, {
        scope: PermissionScope.Self,
    });

    const canViewOperationHistory = useRoleAccess(CompanyAccess.WarehouseOperationEditHistoryView);
    const canDeleteRestoreWarehouse = useRoleAccess(CompanyAccess.WarehouseDeleteRestore);

    const resolveOperationEditAccess = useCallback(
        (operation?: StorageProductRelationHistoryProxy | null): boolean => {
            const targetEmployeeId = operation?.historyCreator?.id;

            switch (operation?.historyAction) {
                case StorageProductRelationHistoryAction.Arrival:
                    return resolveScopedAccess(currentBranchEmployeeId, targetEmployeeId, {
                        all: canEditArrivalAll,
                        self: canEditArrivalSelf,
                    });
                case StorageProductRelationHistoryAction.BookingWriteOff:
                    return resolveScopedAccess(currentBranchEmployeeId, targetEmployeeId, {
                        all: canEditConsumablesWriteOffAll,
                        self: canEditConsumablesWriteOffSelf,
                    });
                case StorageProductRelationHistoryAction.Movement:
                    return resolveScopedAccess(currentBranchEmployeeId, targetEmployeeId, {
                        all: canEditMoveAll,
                        self: canEditMoveSelf,
                    });
                case StorageProductRelationHistoryAction.Sale:
                    return resolveScopedAccess(currentBranchEmployeeId, targetEmployeeId, {
                        all: canEditSaleAll,
                        self: canEditSaleSelf,
                    });
                case StorageProductRelationHistoryAction.WriteOff:
                    return resolveScopedAccess(currentBranchEmployeeId, targetEmployeeId, {
                        all: canEditProductWriteOffAll,
                        self: canEditProductWriteOffSelf,
                    });
                default:
                    return false;
            }
        },
        [
            canEditArrivalAll,
            canEditArrivalSelf,
            canEditConsumablesWriteOffAll,
            canEditConsumablesWriteOffSelf,
            canEditMoveAll,
            canEditMoveSelf,
            canEditProductWriteOffAll,
            canEditProductWriteOffSelf,
            canEditSaleAll,
            canEditSaleSelf,
            currentBranchEmployeeId,
        ]
    );

    return {
        canViewOperationHistory,
        canDeleteRestoreWarehouse,
        canEditOperation: resolveOperationEditAccess,
        resolveOperationEditAccess,
    };
};
